import { z } from 'zod';

/** Evidence band for repo-derived extractions (matches 3C vocabulary). */
export const RepoEvidenceBandSchema = z.enum(['available', 'limited', 'insufficient_evidence']);
export type RepoEvidenceBand = z.infer<typeof RepoEvidenceBandSchema>;

export const RepoProposalStatusSchema = z.enum(['proposed', 'confirmed', 'dismissed']);
export type RepoProposalStatus = z.infer<typeof RepoProposalStatusSchema>;

export const RepoProposalTypeSchema = z.enum(['route', 'api']);
export type RepoProposalType = z.infer<typeof RepoProposalTypeSchema>;

/** Optional apidoc JSDoc metadata attached during extraction. */
export const ApidocParamMetaSchema = z.object({
  name: z.string().min(1),
  in: z.string().optional(),
  type: z.string().optional(),
  optional: z.boolean().optional(),
});
export type ApidocParamMeta = z.infer<typeof ApidocParamMetaSchema>;

export const ApidocMetaSchema = z.object({
  apiName: z.string().optional(),
  apiGroup: z.string().optional(),
  params: z.array(ApidocParamMetaSchema).optional(),
});
export type ApidocMeta = z.infer<typeof ApidocMetaSchema>;

export const RepoProposalEvidenceSchema = z.object({
  extractorId: z.string().min(1).max(80),
  extractorVersion: z.string().min(1).max(20).default('1'),
  sourcePath: z.string().min(1).max(500),
  sourceLine: z.number().int().positive().optional(),
  workspaceUnit: z.string().min(1).max(200),
  band: RepoEvidenceBandSchema,
  scanJobId: z.string().uuid().optional(),
  apidoc: ApidocMetaSchema.optional(),
});
export type RepoProposalEvidence = z.infer<typeof RepoProposalEvidenceSchema>;

export const RepoKnowledgeProposalSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  proposalType: RepoProposalTypeSchema,
  status: RepoProposalStatusSchema,
  workspaceUnit: z.string(),
  sourcePath: z.string(),
  sourceLine: z.number().int().positive().optional(),
  /** Route proposals — TIA glob pattern. */
  glob: z.string().optional(),
  /** Route proposals — application route path. */
  route: z.string().optional(),
  /** API proposals — OpenAPI-style path. */
  apiPath: z.string().optional(),
  apiMethod: z.string().optional(),
  confidence: z.number().min(0).max(1),
  evidence: RepoProposalEvidenceSchema,
  discoveryId: z.string().uuid().nullable().optional(),
  updatedAt: z.string(),
});
export type RepoKnowledgeProposal = z.infer<typeof RepoKnowledgeProposalSchema>;

export const ListRepoKnowledgeProposalsResponseSchema = z.object({
  projectId: z.string().uuid(),
  proposals: z.array(RepoKnowledgeProposalSchema),
  proposedCount: z.number().int().min(0),
});
export type ListRepoKnowledgeProposalsResponse = z.infer<typeof ListRepoKnowledgeProposalsResponseSchema>;

export const ReviewRepoProposalRequestSchema = z.object({
  status: z.enum(['confirmed', 'dismissed']),
});
export type ReviewRepoProposalRequest = z.infer<typeof ReviewRepoProposalRequestSchema>;

export const ExtractRepoKnowledgeRequestSchema = z.object({
  jobId: z.string().uuid(),
});
export type ExtractRepoKnowledgeRequest = z.infer<typeof ExtractRepoKnowledgeRequestSchema>;

export const ExtractRepoKnowledgeResponseSchema = z.object({
  projectId: z.string().uuid(),
  jobId: z.string().uuid(),
  proposalCount: z.number().int().min(0),
  routeCount: z.number().int().min(0),
  apiCount: z.number().int().min(0),
});
export type ExtractRepoKnowledgeResponse = z.infer<typeof ExtractRepoKnowledgeResponseSchema>;

/** Workspace/application unit from scan metadata (deterministic). */
export const WorkspaceUnitSchema = z.object({
  relativePath: z.string(),
  label: z.string(),
  marker: z.string().optional(),
  kind: z.string().optional(),
});
export type WorkspaceUnit = z.infer<typeof WorkspaceUnitSchema>;

export const RouteExtractionDraftSchema = z.object({
  route: z.string().min(1),
  glob: z.string().min(1),
  sourcePath: z.string().min(1),
  sourceLine: z.number().int().positive().optional(),
  workspaceUnit: z.string(),
  extractorId: z.string(),
  band: RepoEvidenceBandSchema,
  confidence: z.number().min(0).max(1),
});
export type RouteExtractionDraft = z.infer<typeof RouteExtractionDraftSchema>;

export const ApiExtractionDraftSchema = z.object({
  apiPath: z.string().min(1),
  apiMethod: z.string().optional(),
  sourcePath: z.string().min(1),
  sourceLine: z.number().int().positive().optional(),
  workspaceUnit: z.string(),
  extractorId: z.string(),
  band: RepoEvidenceBandSchema,
  confidence: z.number().min(0).max(1),
  apidoc: ApidocMetaSchema.optional(),
});
export type ApiExtractionDraft = z.infer<typeof ApiExtractionDraftSchema>;
